import { useQuery } from '@tanstack/react-query'

import type { GetToken } from '~/config'
import { createClient } from '~/data/graphql'
import {
  fetchAgentAccount,
  fetchAgentActivities,
  fetchAgentRecentMessages,
  fetchPersonTasks,
} from '~/data/interact'
import { fetchMemoryCategories } from '~/data/memory'
import { parseApiTime } from '~/data/meetings'
import type { Person } from '~/data/types'
import { theme } from '~/theme'

// The deep peek on a person card: their REAL workload (assigned tasks), and for
// AI agents a live "what it's been doing" feed (activities + recent posts) —
// the presence/trust layer for a human+AI team.

const STATUS_COLOR: Record<string, string> = {
  TODO: '#8b93a7',
  INPROGRESS: '#e0a13a',
  COMPLETED: '#54d18c',
  OVERDUE: '#ff5a5a',
  CANCELLED: '#6a6a72',
}
const STATUS_SHORT: Record<string, string> = {
  TODO: 'To do',
  INPROGRESS: 'Doing',
  COMPLETED: 'Done',
  OVERDUE: 'Overdue',
  CANCELLED: 'Cancelled',
}

function ago(createdAt: string): string {
  const ms = parseApiTime(createdAt)
  if (Number.isNaN(ms)) return ''
  const mins = Math.max(0, Math.round((Date.now() - ms) / 60_000))
  if (mins < 1) return 'now'
  if (mins < 60) return `${mins}m`
  const hours = Math.round(mins / 60)
  if (hours < 24) return `${hours}h`
  return `${Math.round(hours / 24)}d`
}

export function PersonPeek({
  person,
  organizationId,
  getToken,
  isOwner,
}: {
  person: Person
  organizationId: string
  getToken: GetToken
  /** Agent feeds are owner-gated server-side; skip the calls for non-owners. */
  isOwner: boolean
}) {
  const client = async () => createClient(await getToken())

  const tasks = useQuery({
    queryKey: ['personTasks', organizationId, person.userId],
    queryFn: async () =>
      fetchPersonTasks(await client(), { organizationId, userId: person.userId }),
    staleTime: 60_000,
    refetchOnWindowFocus: false,
    retry: false,
  })

  const agentEnabled = person.isAgent && isOwner
  const account = useQuery({
    queryKey: ['agentAccount', organizationId, person.userId],
    queryFn: async () =>
      fetchAgentAccount(await client(), { organizationId, userId: person.userId }),
    enabled: agentEnabled,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
    retry: false,
  })
  const agentAccountId = account.data?.id

  const activities = useQuery({
    queryKey: ['agentActivities', agentAccountId],
    queryFn: async () => fetchAgentActivities(await client(), agentAccountId!, 6),
    enabled: Boolean(agentAccountId),
    refetchInterval: 15_000,
    retry: false,
  })
  const recent = useQuery({
    queryKey: ['agentRecentMessages', agentAccountId],
    queryFn: async () => fetchAgentRecentMessages(await client(), agentAccountId!, 4),
    enabled: Boolean(agentAccountId),
    refetchInterval: 20_000,
    retry: false,
  })
  const memory = useQuery({
    queryKey: ['agentMemCats', agentAccountId],
    queryFn: async () =>
      fetchMemoryCategories(await client(), {
        organizationId,
        scopeType: 'AGENT',
        scopeId: agentAccountId!,
      }),
    enabled: Boolean(agentAccountId),
    staleTime: 60_000,
    retry: false,
  })
  const factCount = (memory.data ?? []).reduce((sum, c) => sum + c.count, 0)

  const openTasks = (tasks.data ?? []).filter(
    (t) => t.status !== 'COMPLETED' && t.status !== 'CANCELLED',
  )

  return (
    <div>
      {/* Their real workload */}
      {openTasks.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <div style={sectionTitle}>Working on</div>
          {openTasks.slice(0, 6).map((t) => (
            <div key={t.id} style={row}>
              <span
                style={{
                  ...chip,
                  color: t.statusColor ?? STATUS_COLOR[t.status] ?? theme.textDim,
                }}
              >
                {t.statusLabel ?? STATUS_SHORT[t.status] ?? t.status}
              </span>
              <span style={rowText} title={t.title}>
                {t.title}
              </span>
            </div>
          ))}
          {openTasks.length > 6 && (
            <div style={{ fontSize: 10.5, color: theme.textDim, marginTop: 3 }}>
              +{openTasks.length - 6} more
            </div>
          )}
        </div>
      )}

      {/* What the agent remembers (Zunou Memory, agent scope). */}
      {agentEnabled && factCount > 0 && (
        <div style={{ marginTop: 10, fontSize: 11, color: theme.textDim }}>
          🧠 {factCount} fact{factCount === 1 ? '' : 's'} remembered ·{' '}
          {(memory.data ?? [])
            .slice(0, 3)
            .map((c) => c.category)
            .join(', ')}
        </div>
      )}

      {/* What the agent has actually been doing (live-ish) */}
      {agentEnabled && (activities.data?.length ?? 0) > 0 && (
        <div style={{ marginTop: 12 }}>
          <div style={sectionTitle}>
            Recent work
            <span style={livePulse} />
          </div>
          {activities.data!.slice(0, 6).map((a) => (
            <div key={a.id} style={row}>
              <span style={{ fontSize: 10, color: theme.textDim, flex: '0 0 26px' }}>
                {ago(a.createdAt)}
              </span>
              <span style={rowText} title={a.summary ?? a.type}>
                {a.label || a.summary || a.type}
                {a.status ? (
                  <span style={{ color: theme.textDim }}> · {a.status.toLowerCase()}</span>
                ) : null}
              </span>
            </div>
          ))}
        </div>
      )}

      {agentEnabled && (recent.data?.length ?? 0) > 0 && (
        <div style={{ marginTop: 12 }}>
          <div style={sectionTitle}>Recent posts</div>
          {recent.data!.map((m) => (
            <div key={m.id} style={{ padding: '3px 0' }}>
              <div style={{ fontSize: 10, color: theme.textDim }}>
                {ago(m.createdAt)} ago{m.topicName ? ` · #${m.topicName}` : ''}
              </div>
              <div
                style={{
                  fontSize: 11.5,
                  color: theme.text,
                  overflow: 'hidden',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical' as never,
                }}
              >
                {m.text || '…'}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

const sectionTitle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  fontSize: 10.5,
  fontWeight: 700,
  letterSpacing: 0.4,
  textTransform: 'uppercase',
  color: theme.textDim,
  marginBottom: 5,
}
const livePulse: React.CSSProperties = {
  width: 6,
  height: 6,
  borderRadius: '50%',
  background: theme.online,
  animation: 'go-live-pulse 1.6s ease-in-out infinite',
}
const row: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 7,
  padding: '3px 0',
}
const rowText: React.CSSProperties = {
  flex: 1,
  minWidth: 0,
  fontSize: 11.5,
  color: theme.text,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}
const chip: React.CSSProperties = {
  flex: '0 0 auto',
  fontSize: 9.5,
  fontWeight: 700,
  padding: '2px 6px',
  borderRadius: 5,
  background: '#101218',
  border: `1px solid ${theme.border}`,
}
